<template>
    <article
        class="property"
        :data-show-loading-state="!loadingStateShown"
    >
        <MainHero
            key="property-hero"
            :background-color="entry?.propertyHero?.[0]?.backgroundColor || null"
            variant="property"
            :images="entry?.propertyHero?.[0]?.images"
            :video-mp4="entry?.propertyHero?.[0]?.videoMp4"
            :video-web-m="entry?.propertyHero?.[0]?.videoWebM"
            :position-video="entry?.propertyHero?.[0]?.positionVideo"
            :poster="entry?.propertyHero?.[0]?.videoPlaceholderImage"
            :pending="pending"
            :loading-state-shown="loadingStateShown"
        >
            <template #cta>
                <BaseIconList
                    v-if="entry?.facilities && entry.facilities.length"
                    :items="entry?.facilities"
                    variant="list"
                    class="base-list--truncate-sm"
                />

                <CallToAction parent-component="MainHero">
                    <template
                        v-if="entry?.city?.[0]?.title"
                        #title
                    >
                        <DashedText
                            class="property__title"
                            element="h1"
                        >
                            <template
                                v-if="entry?.city?.[0]?.title"
                                #left
                            >
                                {{ entry.city[0].title }}
                            </template>

                            <template
                                v-if="entry?.area"
                                #right
                            >
                                {{ entry.area }}
                            </template>
                        </DashedText>
                    </template>

                    <template #description>
                        {{ entry?.propertyHero?.[0]?.description }}
                    </template>

                    <template
                        v-if="entry?.propertyPhase !== 'inDevelopment'"
                        #cta
                    >
                        {{ $t('check-availability') }}
                    </template>
                </CallToAction>
            </template>
        </MainHero>

        <ScrollNavigation
            :pending="pending"
            :entry="entry"
            :items="[
                {
                    entryField: 'apartmentsBlock',
                    label: 'apartments',
                    id: null
                },
                {
                    entryField: 'carousselBlock',
                    label: 'facilities',
                    id: null
                },
                {
                    entryField: 'sliderBlock',
                    label: 'restaurant-bar',
                    id: null
                },
                {
                    entryField: 'promoBlock',
                    label: 'meeting-events',
                    id: null
                },
                {
                    entryField: 'textImage',
                    label: 'contact',
                    id: null
                },
            ]"
        />

        <ApartmentList
            v-if="pending"
            class="property__section"
        >
            <template #title>
                <Skeletor />
            </template>

            <template #description>
                <Skeletor />
                <Skeletor />
                <Skeletor />
            </template>

            <template #caption>
                <Skeletor />
            </template>

            <template #images>
                <template v-if="pending">
                    <Skeletor
                        v-for="i in [1,2,3]"
                        :key="i"
                        height="40vw"
                        :shimmer="false"
                        class="apartment-list__image"
                    />
                </template>
            </template>
        </ApartmentList>

        <ApartmentList
            v-else-if="entry?.id"
            :id="kebabCase(entry?.apartmentsBlock?.[0]?.title)"
            :pending="pending"
            :property-id="entry?.id"
            :images="entry?.apartmentsBlock?.[0]?.images"
            :background-color="entry?.apartmentsBlock?.[0]?.backgroundColor"
            :show-review-summary="entry?.showReviewSummary"
            class="property__section"
        >
            <template #title>
                {{ entry?.apartmentsBlock?.[0]?.title }}
            </template>

            <template #description>
                {{ entry?.apartmentsBlock?.[0]?.description }}
            </template>

            <template #caption>
                {{ entry?.apartmentsBlock?.[0]?.caption }}
            </template>
        </ApartmentList>

        <CarousselBlock
            v-if="entry?.carousselBlock?.[0]?.title || pending"
            :id="kebabCase(entry?.carousselBlock?.[0]?.title)"
            class="property__section"
            variant="property"
            :caroussel-items="entry?.carousselBlock?.[0]?.carousselItems"
            :background-color="entry?.carousselBlock?.[0]?.backgroundColor"
            :pending="pending"
        >
            <template
                v-if="entry?.carousselBlock?.[0]?.title"
                #title
            >
                {{ entry?.carousselBlock?.[0]?.title }}
            </template>

            <template
                v-if="entry?.facilities && entry.facilities.length"
                #description
            >
                <BaseIconList
                    :items="entry.facilitiesShowcase"
                    variant="grid"
                />
            </template>
        </CarousselBlock>

        <IllustrationGrid
            v-if="entry?.illustrationGrid?.[0]?.illustrationItems?.length"
            :items="entry?.illustrationGrid?.[0]?.illustrationItems"
        >
            <template #title>{{ entry?.illustrationGrid?.[0]?.title }}</template>
            <template #description>{{ entry?.illustrationGrid?.[0]?.description }}</template>

            <IllustrationItem
                v-for="illustration in entry?.illustrationGrid?.[0]?.illustrationItems"
                :key="illustration?.title"
                :illustration="illustration"
                :class="illustration?.sticker?.length > 12 ? 'sticker__title--small' : ''"
            >
                <template
                    v-if="illustration?.sticker"
                    #offer
                >
                    {{ illustration?.sticker }}
                </template>
                <template #title>
                    {{ illustration?.title }}
                </template>
                <template #description>
                    {{ illustration?.description }}
                </template>
                <template #cta>
                    {{ illustration?.linkField?.text }}
                </template>
            </IllustrationItem>
        </IllustrationGrid>

        <SliderBlock
            v-if="entry?.sliderBlock?.[0]?.slides?.length || pending"
            :id="kebabCase(entry?.sliderBlock?.[0]?.title)"
            :images="entry?.sliderBlock?.[0]?.slides"
            :pending="pending"
            :background-color="entry?.sliderBlock?.[0]?.backgroundColor"
            :custom-background-color="entry?.sliderBlock?.[0]?.customBackgroundColor"
            class="property__section"
            :is-light="true"
        >
            <template
                v-if="entry?.sliderBlock?.[0]?.title"
                #title
            >
                {{ entry?.sliderBlock?.[0]?.title }}
            </template>

            <template
                v-if="entry?.sliderBlock?.[0]?.caption"
                #description
            >
                {{ entry?.sliderBlock?.[0]?.caption }}
            </template>
        </SliderBlock>

        <RelatedItems
            :id="kebabCase(entry?.relatedItems?.[0]?.title)"
            class="property__section"
            :background-color="entry?.relatedItems?.[0]?.backgroundColor"
            :items="entry?.relatedItems?.[0]?.entries"
            :pending="pending"
        >
            <template
                v-if="entry?.relatedItems?.[0]?.title"
                #title
            >
                {{ entry.relatedItems[0].title }}
            </template>

            <template
                v-if="entry?.relatedItems?.[0]?.caption"
                #caption
            >
                {{ entry.relatedItems[0].caption }}
            </template>
        </RelatedItems>

        <MapBlock
            v-if="entry?.mapBlock?.[0]?.markers || pending"
            :markers="entry?.mapBlock?.[0]?.markers"
        >
            <template
                v-if="entry?.mapBlock?.[0]?.description"
                #description
            >
                {{ entry.mapBlock[0].description }}
            </template>
        </MapBlock>

        <StorytellingBlock
            v-if="entry?.storytellingBlock?.[0]?.title || pending"
            :id="kebabCase(entry?.storytellingBlock?.[0]?.title)"
            :images="entry?.storytellingBlock?.[0]?.images"
            :background-color="entry?.storytellingBlock?.[0]?.backgroundColor"
            :pending="pending"
            class="property__section"
        >
            <template
                v-if="entry?.storytellingBlock?.[0]?.title"
                #title
            >
                {{ entry?.storytellingBlock?.[0].title }}
            </template>

            <template
                v-if="entry?.storytellingBlock?.[0]?.description"
                #description
            >
                {{ entry?.storytellingBlock?.[0].description }}
            </template>

            <template
                v-if="entry?.storytellingBlock?.[0]?.link"
                #link
            >
                <BaseArrowLink
                    :element="CraftLink"
                    :link="entry?.storytellingBlock?.[0].link"
                >
                    {{ entry?.storytellingBlock?.[0].link.text }}
                </BaseArrowLink>
            </template>
        </StorytellingBlock>

        <template v-if="entry?.promoBlock?.[0]?.title || entry?.promoBlock?.[0]?.image?.[0]">
            <!-- Move the anchor here because overflow messes up the scroll-margin -->
            <div
                id="meetings-events"
                class="property__section"
            />
            <PromoBlock
                v-for="(promoBlockRow, index) in entry?.promoBlock"
                :key="index"
                :background-color="promoBlockRow?.backgroundColor"
                :pending="pending"
                :image="promoBlockRow?.image?.[0]"
                :image-position="promoBlockRow?.imagePosition"
                :link="promoBlockRow?.linkField"
            >
                <template
                    v-if="promoBlockRow?.title"
                    #title
                >
                    {{ promoBlockRow?.title }}
                </template>

                <template
                    v-if="promoBlockRow?.description"
                    #description
                >
                    {{ promoBlockRow?.description }}
                </template>

                <template
                    v-if="promoBlockRow?.linkField?.url || promoBlockRow?.linkField?.element"
                    #text
                >
                    <BaseArrowLink
                        :element="CraftLink"
                        :link="promoBlockRow?.linkField"
                        label-position="right"
                    >
                        {{ promoBlockRow?.linkField.text }}
                    </BaseArrowLink>
                </template>
            </PromoBlock>
        </template>

        <BaseSection
            v-if="entry?.faq?.[0]?.items?.length || pending"
            :id="kebabCase(entry?.faq?.[0]?.title)"
            :pending="pending"
            class="property__section"
        >
            <template
                v-if="entry?.faq?.[0]?.title"
                #title
            >
                {{ entry?.faq?.[0]?.title }}
            </template>
            <BaseAccordion :items="entry?.faq?.[0]?.items" />
        </BaseSection>

        <DelayHydration>
            <BaseTextImage
                v-if="entry?.textImage?.[0]?.title || pending"
                :id="kebabCase(entry?.textImage?.[0]?.title)"
                class="property__section base-text-image--plain-link"
                :background-color="entry?.textImage?.[0]?.backgroundColor"
                :image="entry?.textImage?.[0]?.image?.[0]"
                :pending="pending"
                :landscape="true"
                :use-parallax="true"
            >
                <template #title>
                    {{ entry?.textImage?.[0]?.title }}
                </template>

                <template
                    #text
                >
                    <!-- eslint-disable-next-line vue/no-v-html -->
                    <div v-html="entry?.textImage?.[0]?.text" />
                </template>
            </BaseTextImage>
        </DelayHydration>
    </article>
</template>

<script setup>
import { kebabCase } from 'lodash-es';
import propertyQuery from '~/graphql/queries/property.graphql';

const { variables } = useCraftGraphql();
const response = await useLazyAsyncQuery({
    query: propertyQuery,
    variables
});
const pending = response.pending;
const { craftEntry: entry } = useCraftPage(response);

const CraftLink = resolveComponent('CraftLink');

// /** Show a loading state ONLY once per SPA session **/
const loadingStateShown = useState('loadAnimationShown', () => typeof loadingAnimationEnded === 'undefined' ? false : loadingAnimationEnded);

onMounted(() => {
    if (!loadingStateShown.value) {
        // Listen for the end of the transition and check the non-reactive `loadingAnimationEnded` in case we're on a slow machine.
        if (typeof loadingAnimationEnded === 'undefined' || loadingAnimationEnded === false) {
            document.querySelector('.main-hero').addEventListener('animationend', () => {
                setStateShown();
            });
        } else if (loadingAnimationEnded === true) {
            setStateShown();
        }
    }
});

// Inject this script into the head so it can fire before hydration even starts. Otherwise, on slow machines, the animation finished before we can even listen for it.
useHead({
    script: [
        {
            innerHTML: `
                var loadingAnimationEnded = false;
                window.addEventListener('animationend', (event) => {
                    if (event.animationName === 'topElementFade' && event.target.className === 'main-hero__cta') {
                        loadingAnimationEnded = true;
                    }
                });
            `
        }
    ]
});

function setStateShown() {
    loadingStateShown.value = true;
}
</script>

<style lang="less">
.property__title {
    .typography-h4();
}

.property__section {
    scroll-margin: calc(var(--navbar-height) + 3.5rem - 1px); // -1px to account for subpixel rounding
}

@media ( prefers-reduced-motion: no-preference ) {
    .property[data-show-loading-state='true'] {
        .animation-main-page();
    }
}
</style>

